import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import Header from 'shared/components/Header';
import { tokens } from 'shared/theme';
import { useTenantPath } from 'shared/hooks/useTenantPath';
import { useAuth } from 'shared/hooks/useAuth';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { PERMISSIONS } from 'platform/rbac/Permissions';

const HUB_ACTIONS = [
  {
    key: 'view-all',
    label: 'Add and View all registered Systems',
    description: 'Register new systems, then browse, search, edit, and manage all registered systems.',
    to: '/system-register/software',
    icon: ViewListOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewSystems,
  },
  {
    key: 'manage-details',
    label: 'Manage System Details',
    description: 'Maintain business units, owners, vendors, regulations, data records, reviews, and more.',
    to: '/system-register/manage-details',
    icon: TuneOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewSystemDetails,
  },
  {
    key: 'risk-register',
    label: 'Risk Register',
    description: 'Record, assess, and track risks across registered systems.',
    to: '/system-register/risk-register',
    icon: WarningAmberOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewRiskRegister,
  },
  {
    key: 'it-asset-register',
    label: 'IT Asset Register',
    description: 'Keep an inventory of IT assets used by the organization.',
    to: '/system-register/it-asset-register',
    icon: DevicesOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewAssetRegister,
  },
  {
    key: 'sop',
    label: 'IT Documents',
    description: 'Store and manage IT documents, with or without a linked software system.',
    to: '/system-register/sop',
    icon: MenuBookOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewSop,
  },
  {
    key: 'change-management',
    label: 'Change Management',
    description: 'Plan, approve, and track changes to registered systems.',
    to: '/system-register/change-managements',
    icon: PublishedWithChangesOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewChangeManagement,
  },
  {
    key: 'monitoring-review',
    label: 'Monitoring and Review',
    description: 'Schedule reviews and record monitoring findings.',
    to: '/system-register/reviews',
    icon: FactCheckOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewMonitoringReview,
  },
  {
    key: 'incident-management',
    label: 'Incident Management',
    description: 'Log incidents, severity, and the actions taken to resolve them.',
    to: '/system-register/incidents',
    icon: ReportProblemOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewIncidentManagement,
  },
  {
    key: 'access-provision',
    label: 'Access Administration',
    description: 'Access Administration, Modification and Removal management',
    to: '/system-register/access-provisions',
    icon: VpnKeyOutlinedIcon,
    permission: PERMISSIONS.systemRegister.viewAccessProvision,
  },
];

const PAGE_NAME = 'IT Governance';

function isItGovernanceAdmin(profile) {
  const names = [];
  if (Array.isArray(profile?.roles) && profile.roles.length > 0) {
    names.push(...profile.roles);
  } else if (profile?.role) {
    names.push(profile.role);
  }
  return names.some((roleName) => {
    const normalized = String(roleName ?? '')
      .trim()
      .toUpperCase()
      .replace(/[\s_-]+/g, '');
    return (
      normalized === 'ADMIN'
      || normalized === 'SYSTEMADMIN'
      || normalized === 'SERVICEADMIN'
    );
  });
}

const SystemRegisterPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { tp } = useTenantPath();
  const { user } = useAuth();
  const profile = user?.data;
  const visibleActions = useMemo(
    () => (
      isItGovernanceAdmin(profile)
        ? HUB_ACTIONS
        : HUB_ACTIONS.filter((item) => sessionHasPermission(profile, item.permission))
    ),
    [profile]
  );
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;
  const secondaryMain = theme.palette.secondary.main;

  const cardSurface = isDark ? theme.palette.background.paper : '#ffffff';
  const titleColor = isDark ? colors.grey[100] : primaryMain;
  /** All description / secondary text — pure black in light mode. */
  const bodyColor = isDark ? colors.grey[100] : '#000000';

  const heroGradient = useMemo(
    () =>
      isDark
        ? `linear-gradient(135deg, ${alpha(primaryMain, 0.35)} 0%, ${colors.primary[400]} 45%, ${alpha(secondaryMain, 0.22)} 100%)`
        : `linear-gradient(135deg, ${alpha(primaryMain, 0.12)} 0%, #ffffff 55%, ${alpha(secondaryMain, 0.1)} 100%)`,
    [isDark, primaryMain, secondaryMain, colors.primary]
  );

  return (
    <Box m={{ xs: '8px', sm: '12px' }}>
      <Header title={PAGE_NAME} />

      <Box
        mt={0.5}
        mb={1.25}
        px={{ xs: 1.25, sm: 1.5 }}
        py={{ xs: 0.75, sm: 1 }}
        borderRadius={1.25}
        sx={{
          background: heroGradient,
          border: `1px solid ${alpha(primaryMain, isDark ? 0.45 : 0.18)}`,
          boxShadow: isDark ? 'none' : `0 2px 12px ${alpha(primaryMain, 0.06)}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(secondaryMain, isDark ? 0.25 : 0.14),
              color: secondaryMain,
            }}
          >
            <AppRegistrationOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box flex={1} minWidth={140}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: titleColor, lineHeight: 1.2 }}
            >
              {PAGE_NAME}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: bodyColor,
                display: 'block',
                lineHeight: 1.3,
                fontSize: '0.75rem',
                mt: 0.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Register and govern systems, assets, documents, risks, reviews, changes, incidents, integrations, access, and approvals.
            </Typography>
          </Box>
          <Chip
            label={PAGE_NAME}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: secondaryMain,
              color: theme.palette.secondary.contrastText,
            }}
          />
        </Box>
      </Box>

      {visibleActions.length === 0 ? (
        <Typography variant="body2" sx={{ color: bodyColor }}>
          You do not have permission to view any IT Governance cards.
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          alignItems: 'stretch',
        }}
      >
        {visibleActions.map((item) => {
          const Icon = item.icon;

          return (
            <Card
              key={item.key}
              elevation={0}
              sx={{
                height: '100%',
                minHeight: 108,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: cardSurface,
                border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
                borderRadius: 1.5,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                boxShadow: isDark ? 'none' : `0 1px 8px ${alpha(primaryMain, 0.05)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  borderColor: secondaryMain,
                  boxShadow: `0 6px 16px ${alpha(secondaryMain, isDark ? 0.18 : 0.12)}`,
                },
              }}
            >
              <CardActionArea
                component={Link}
                to={tp(item.to)}
                sx={{
                  color: 'inherit',
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'stretch',
                  '&:hover': {
                    backgroundColor: alpha(secondaryMain, isDark ? 0.1 : 0.04),
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 1.25,
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    '&:last-child': { pb: 1.25 },
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: alpha(primaryMain, isDark ? 0.22 : 0.1),
                        color: isDark ? colors.grey[100] : primaryMain,
                        border: `1px solid ${alpha(primaryMain, isDark ? 0.3 : 0.16)}`,
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </Box>

                    <Box flex={1} minWidth={0} display="flex" flexDirection="column">
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          color: titleColor,
                          fontSize: '0.82rem',
                          lineHeight: 1.25,
                          mb: 0.35,
                          minHeight: '2.5em',
                          maxHeight: '2.5em',
                          overflow: 'hidden',
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: bodyColor,
                          lineHeight: 1.35,
                          fontSize: '0.68rem',
                          minHeight: '2.7em',
                          maxHeight: '2.7em',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>

                    <ArrowForwardIcon
                      sx={{
                        fontSize: 16,
                        mt: 0.25,
                        flexShrink: 0,
                        color: alpha(secondaryMain, 0.85),
                      }}
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};


export default SystemRegisterPage;
